import { assertUnreachable } from '@myre/common-primitives';

import { inject, Injectable } from '@angular/core';
import { DomSanitizer, type SafeHtml } from '@angular/platform-browser';
import DOMPurify from 'dompurify';
import katex from 'katex';
import MarkdownIt from 'markdown-it';
// @ts-expect-error katex is not typed
import texmath from 'markdown-it-texmath';

import { FormatPartType } from '../interfaces/chat.interfaces';

import { HtmlPreviewService } from './html-preview.service';

/**
 * Result of parsing text, containing both the rendered HTML and extracted HTML blocks.
 */
export interface ParseResult {
  /** The rendered SafeHtml for display */
  html: SafeHtml;
  /** The last HTML block content (if any) for preview */
  lastHtmlBlock: string | null;
}

/**
 * One renderable piece of a message's text, in source order. A `markdown`
 * segment is a flow of markdown/code rendered as inline HTML; a `copy` segment
 * is a self-contained block the agent marked as independently copyable (via a
 * ```copy fence), carrying both its display HTML and the raw text to copy.
 */
export type RenderSegment =
  | { readonly type: 'markdown'; readonly html: SafeHtml }
  | { readonly type: 'copy'; readonly text: string; readonly html: SafeHtml };

/** Result of {@link FormatService.parseSegments}. */
export interface SegmentedParseResult {
  /** Renderable segments in source order. */
  readonly segments: readonly RenderSegment[];
  /** The last HTML block content (if any) for preview. */
  readonly lastHtmlBlock: string | null;
}

/** A run of lines of a single kind, extracted from a fenced-block scan. */
interface FormatPart {
  type: FormatPartType;
  content: string[];
}

@Injectable({
  providedIn: 'root',
})
export class FormatService {
  private _domSanitizer = inject(DomSanitizer);
  private _htmlPreviewService = inject(HtmlPreviewService);
  private _markdownIt = new MarkdownIt({
    html: true,
    linkify: true,
    typographer: true,
  }).use(texmath, {
    engine: katex,
    delimiters: 'dollars',
  });

  public parse(text: string): SafeHtml {
    return this.parseWithHtmlBlocks(text).html;
  }

  /**
   * Sanitizes a raw HTML string and returns it as a `SafeHtml` value.
   * Use for injecting arbitrary HTML snippets that are not going through the
   * full markdown pipeline (e.g., extracted `<details>` blocks).
   */
  public sanitizeHtml(html: string): SafeHtml {
    return this._domSanitizer.bypassSecurityTrustHtml(
      DOMPurify.sanitize(html, {
        ADD_TAGS: ['details', 'summary'],
      }),
    );
  }

  /**
   * Parses text and returns both rendered HTML and extracted HTML blocks.
   * Use this when you need access to HTML blocks for preview functionality.
   *
   * When `citationMarkers` is provided, inline `[n]` occurrences for markers
   * present in that set are wrapped in `<sup class="citation-marker">n</sup>`
   * **before** DOMPurify runs, so the wrapper survives sanitisation and
   * `sup` + the class attribute are preserved via the explicit allowlist.
   * Without `citationMarkers` the text is left unchanged.
   */
  public parseWithHtmlBlocks(
    text: string,
    citationMarkers?: ReadonlySet<number>,
  ): ParseResult {
    const parts = this._parseTextIntoParts(text);

    return {
      html: this._renderParts(parts, citationMarkers),
      lastHtmlBlock: this._extractLastHtmlBlock(parts),
    };
  }

  /**
   * Parses text into ordered renderable segments, splitting out every ```copy
   * block as its own independently-copyable {@link RenderSegment}. Markdown and
   * code between copy blocks are coalesced into `markdown` segments so the flow
   * renders exactly as it would without the splits.
   */
  public parseSegments(
    text: string,
    citationMarkers?: ReadonlySet<number>,
  ): SegmentedParseResult {
    const parts = this._parseTextIntoParts(text);
    const segments: RenderSegment[] = [];
    let buffer: FormatPart[] = [];

    const flushBuffer = (): void => {
      if (buffer.length === 0) {
        return;
      }

      const rawHtml = this._renderPartsRaw(buffer, citationMarkers);
      buffer = [];

      // Skip whitespace-only runs (e.g. the blank lines framing a copy block).
      if (this._hasNoVisibleContent(rawHtml)) {
        return;
      }

      segments.push({
        type: 'markdown',
        html: this._domSanitizer.bypassSecurityTrustHtml(rawHtml),
      });
    };

    for (const part of parts) {
      if (part.type === FormatPartType.Copy) {
        flushBuffer();

        const raw = part.content.join('\n').trim();

        if (raw.length > 0) {
          segments.push({
            type: 'copy',
            text: raw,
            html: this._renderMarkdown(raw),
          });
        }

        continue;
      }

      buffer.push(part);
    }

    flushBuffer();

    return { segments, lastHtmlBlock: this._extractLastHtmlBlock(parts) };
  }

  /** Concatenates the content of every ```html block, or `null` when none. */
  private _extractLastHtmlBlock(parts: readonly FormatPart[]): string | null {
    const htmlParts = parts.filter(p => p.type === FormatPartType.Html);
    const lastHtmlBlock =
      htmlParts.length > 0
        ? htmlParts.map(p => p.content.join('\n')).join('\n')
        : null;

    // Update the preview service with latest HTML (auto-update when window open)
    if (lastHtmlBlock) {
      this._htmlPreviewService.updateIfOpen(lastHtmlBlock);
    }

    return lastHtmlBlock;
  }

  /** Renders a run of parts (markdown/code/html/copy) to sanitized SafeHtml. */
  private _renderParts(
    parts: readonly FormatPart[],
    citationMarkers?: ReadonlySet<number>,
  ): SafeHtml {
    return this._domSanitizer.bypassSecurityTrustHtml(
      this._renderPartsRaw(parts, citationMarkers),
    );
  }

  /** Renders a run of parts to a sanitized HTML string (not yet trusted). */
  private _renderPartsRaw(
    parts: readonly FormatPart[],
    citationMarkers?: ReadonlySet<number>,
  ): string {
    const rawHtml = parts
      .map(part => {
        switch (part.type) {
          case FormatPartType.Markdown: {
            const markdownSource = citationMarkers
              ? this._wrapCitationMarkers(
                  part.content.join('\n'),
                  citationMarkers,
                )
              : part.content.join('\n');

            return this._markdownIt.render(markdownSource);
          }
          case FormatPartType.Code: {
            return `<pre><code>${this._escapeHtml(part.content.join('\n'))}</code></pre>`;
          }
          case FormatPartType.Html:
          case FormatPartType.Copy: {
            // HTML blocks render via the preview button; copy blocks render as
            // their own segment — neither belongs in this inline flow.
            return '';
          }
          default: {
            assertUnreachable(part.type);
          }
        }
      })
      .join('\n');

    return DOMPurify.sanitize(rawHtml, {
      ADD_TAGS: ['sup'],
      ADD_ATTR: ['class'],
    });
  }

  /** Renders a markdown string to sanitized SafeHtml (used for copy blocks). */
  private _renderMarkdown(markdown: string): SafeHtml {
    return this._domSanitizer.bypassSecurityTrustHtml(
      DOMPurify.sanitize(this._markdownIt.render(markdown)),
    );
  }

  /** True when sanitized HTML has no visible text once tags are stripped. */
  private _hasNoVisibleContent(rawHtml: string): boolean {
    return rawHtml.replace(/<[^>]*>/g, '').trim().length === 0;
  }

  private _escapeHtml(text: string): string {
    return text
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#39;');
  }

  /**
   * Replaces `[n]` occurrences in `text` with
   * `<sup class="citation-marker">n</sup>` for each marker number present in
   * `markers`. Bracketed numbers not in the set are left untouched.
   */
  private _wrapCitationMarkers(
    text: string,
    markers: ReadonlySet<number>,
  ): string {
    return text.replace(/\[(\d+)\]/g, (_match, digits: string) => {
      const n = parseInt(digits, 10);

      if (!markers.has(n)) {
        return _match;
      }

      return `<sup class="citation-marker" data-citation-marker="${n}">${n}</sup>`;
    });
  }

  /**
   * Parses text into typed parts (Markdown, Code, Html, Copy).
   * Detects ```html blocks for HTML content, ```copy blocks for
   * independently-copyable content, and other ```language blocks for code.
   */
  private _parseTextIntoParts(text: string): FormatPart[] {
    const parts: FormatPart[] = [
      {
        type: FormatPartType.Markdown,
        content: [],
      },
    ];

    const lines = text.split('\n');

    for (const line of lines) {
      const result = /^```(\w+)?$/.exec(line);

      if (result) {
        const language = result[1]?.toLowerCase();

        if (language === 'html') {
          // Start of an HTML block
          parts.push({
            type: FormatPartType.Html,
            content: [],
          });
        } else if (language === 'copy') {
          // Start of an independently-copyable block
          parts.push({
            type: FormatPartType.Copy,
            content: [],
          });
        } else if (language) {
          // Start of a code block with a language identifier
          parts.push({
            type: FormatPartType.Code,
            content: [],
          });
        } else {
          // End of a code/html block, return to markdown
          parts.push({
            type: FormatPartType.Markdown,
            content: [],
          });
        }
      } else {
        // parts is initialized with at least one element and we only push new
        // elements, never remove any, so `.at(-1)` never returns undefined here.
        parts.at(-1).content.push(line);
      }
    }

    return parts;
  }
}
